<script setup>
import {onMounted, ref} from 'vue';
import sources from '@/data/sources';
import molecules from '@/data/molecules';
import svgParser from '@/tools/parser';
import $ from 'jquery';
import * as d3 from 'd3';
import {useMainStore} from '@/stores/main';
import Section from '@/components/Section.vue';

const store = useMainStore();

const container = ref(null);

const draw = () => {
    var graphics = {
            svg : null,
            moleculeLayer : null,
            textLayer : null,
            buttonLayer : null,
            sourcesLayer : null,
            width : (store.width - 40),
            height : 500,
            buffer : 150,
            frequency : 100,
            speed : 4,
            sourcesLabel : null
        },
        limitLengthWith = 2,
        gridStatus = null,
        ml = ['PM₂.₅', 'SO₂', 'NOₓ', 'NH₃', 'VOC', 'CH₄'],
        statPositions = {
            ml : [],
            src : []
        },
        baseData = [
            [5, 53, 17, 0, 12, 6, 6, 1],
            [48, 14, 24, 9, 0, 1, 0, 4],
            [18, 7, 15, 2, 39, 10, 3, 6],
            [0, 1, 1, 0, 2, 0, 93, 3],
            [3, 26, 41, 6, 11, 3, 9, 1],
            [14, 3, 1, 0, 0, 0, 48, 34]
        ],
        mlStatus = [false, false, false, false, false, false, false, false],
        srcStatus = [false, false, false, false, false, false, false, false, false],
        data = [],
        n = 50,
        loop = null;

    baseData = baseData.map(molecule => molecule.slice(0, molecule.length - 2));

    function stop() {
        clearInterval(loop);
    }

    function play() {
        loop = setInterval(function() {
            float();
        }, graphics.frequency);
    }

    const buildCanvas = () => {
        graphics.svg = d3.select(container.value).append('svg').attr('width', graphics.width).attr('height', graphics.height);
        graphics.sourcesLayer = graphics.svg.append('g')
            .attr('class', 'sources-layer-sources')
            .attr('transform', function(){
                return 'translate(20,' + (graphics.height - 150) + ')';
            });
        graphics.moleculeLayer = graphics.svg.append('g').attr('class', 'sources-layer-molecules');
        graphics.buttonLayer = graphics.svg.append('g').attr('class', 'sources-layer-buttons');
        graphics.textLayer = graphics.svg.append('g').attr('class', 'sources-layer-texts');
    };

    function createData() {
        var offset = 8
            , row = 4
            , molecule
            , sourceY = [0, 0, 0, 0, 0, 0, 0, 0, 0];
        // run through each pollutant
        for (var i = 0, l = baseData.length; i < l; i++) {
            var baseMolecule = baseData[i];
            statPositions.ml[i] = [];
            statPositions.src[i] = [];
            // run through each source in the pollutant
            for (var j = 0, jl = baseMolecule.length; j < jl; j++) {
                var source = baseMolecule[j],
                    // create kl molecules per source in the pollutant
                    kl = kl = Math.ceil(source / 100 * n);
                for (var k = 0; k < kl; k++) {
                    var x = Math.floor(graphics.width * Math.random()),
                        y = Math.floor((graphics.height - graphics.buffer) * Math.random()),
                        gridY = Math.floor(k / row) * offset;
                    molecule = {
                        molecule : i,
                        source : j,
                        gridX: (sources[j].x2) + (k % row) * offset + 45,
                        gridY: gridY,
                        sourceGridY : sourceY[j] + gridY,
                        x: x,
                        y: y,
                        dx : Math.random() - 0.5,
                        dy : Math.random() - 0.5
                    };
                    data.push(molecule);
                }
                statPositions.ml[i][j] = 10; //gridY;
                statPositions.src[i][j] = sourceY[j] + gridY;
                sourceY[j] += yJump(kl, offset, row);
            }
        }
    }

    function yJump(kl, offset, row) {
        var vertical = Math.ceil(kl / row);
        if (vertical === 0 ){
            return 0;
        } else {
            return (vertical + 1) * offset;
        }
    }

    function buildMolecules() {
        graphics.moleculeLayer.selectAll('.sources-molecule').data(data).enter()
            .append('g').attr('class', 'sources-molecule moving').attr('transform', function(d){
                return 'translate(' + d.x + ',' + d.y + ')';
            })
            .append('polygon').attr('points', '3,0 6,3 3,6 0,3').attr('class', function(d){
                return 'ml-color-' + d.molecule;
            });
    }

    function buildButtons() {
        var perRow, offset, paddingLeft;
        perRow = 3;
        offset = 100;
        paddingLeft = 10;
        for (var i = 0; i < baseData.length; i++) {
            var g = graphics.buttonLayer.append('g')
                .attr('transform', function(){
                    var x, y;
                    y = Math.floor(i / perRow) * offset;
                    if (i < perRow) {
                        x = i * offset + paddingLeft;
                    } else {
                        x = (i - perRow) * offset + paddingLeft;
                    }
                    return 'translate(' + x + ',' + y + ')';
                }).attr('ml', i).attr('class', 'sources-ml-button button').on('click', function () {
                    mlGridToggle($(this).attr('ml'));
                });
            svgParser.addIllustration(molecules[i], g);
        }
    }

    function buildSources() {
        graphics.sourcesLayer.append('rect')
            .attr('x', -5)
            .attr('y', 84)
            .attr('fill', '#fff')
            .attr('width', graphics.width - 100)
            .attr('height', 6);
        for (var i = 0, l = (sources.length - limitLengthWith); i < l; i++) {
            var illustration = sources[i]
                , g = graphics.sourcesLayer.append('g').attr('class', 'sources-src-button').attr('transform', function(){
                    return 'translate(' + illustration.x + ', 0)';
                }).attr('src', i).on('click', function () {
                    srcGridToggle($(this).attr('src'));
                });
            svgParser.addIllustration(illustration.p, g);
        }
    }

    function buildStats() {
        for (var i = 0, l = baseData.length; i < l; i++) {
            for (var j = 0, jl = baseData[i].length; j < jl; j++) {
                var stat = baseData[i][j];
                if (stat != 0) {
                    var g = graphics.textLayer.append('g').attr('class', 'sources-stat bold-12 sources-stat-ml-' + i + ' sources-source-' + j).attr('ml', i).attr('src', j);
                    g.append('text').text(ml[i]).attr('x', 0).attr('y', 0).attr('text-anchor', 'end');
                    g.append('text').attr('class', 'sources-percentage').text(stat + '%').attr('x', 0).attr('y', 14).attr('text-anchor', 'end');
                }
            }
        }
    }

    function float() {
        graphics.svg.selectAll('.sources-molecule.moving')
            .transition()
            .duration(graphics.frequency)
            .attr('transform', function(d) {
                translate(d);
                return 'translate(' + d.x + ',' + d.y + ')';
            });
    }

    function translate(d) {
        d.x = d.x + graphics.speed * d.dx;
        d.y = d.y + graphics.speed * d.dy;
        // bounce
        if (d.x >= graphics.width - 6) {
            d.x = graphics.width - 6;
            d.dx *= -1;
        }
        if (d.x <= 6) {
            d.x = 6;
            d.dx *= -1;
        }
        if (d.y >= (graphics.height - graphics.buffer) - 6) {
            d.y = (graphics.height - graphics.buffer)  - 6;
            d.dy *= -1;
        }
        if (d.y <= 6) {
            d.y = 6;
            d.dy *= -1;
        }
    }

    // source to grid functions

    function srcGridToggle(source) {
        if (srcStatus[source]) {
            srcOutGrid(source);
            srcStatus[source] = false;
        } else {
            srcInGrid(source);
            srcStatus[source] = true;
        }
    }

    function setTrue(array, selected) {
        for (var i = 0, l = array.length; i < l; i++) {
            array[i] = (i === selected);
        }
    }

    function srcInGrid(source) {
        if (gridStatus === 2) {
            mlOutGrid();
            srcStatus = [false, false, false, false, false, false, false, false];
        }

        gridStatus = 1;
        var molecules = [false, false, false, false, false, false, false, false];

        graphics.svg.selectAll('.sources-molecule').filter(function(d) {
            if (d.source == source) {
                molecules[d.molecule] = true;
                return this;
            }
        }).attr('class', 'sources-molecule').transition().duration(600).attr('transform', function(d) {
            return 'translate(' + d.gridX + ',' + (graphics.height - 160 - d.sourceGridY) + ')';
        });
        // inactivate button
        $('.sources-src-button').each(function() {
            if ($(this).attr('src') == source) {
                $(this).attr('class', 'sources-src-button button active');
            }
        });
        // buttons inactive
        $('.sources-ml-button').each(function() {
            $(this).attr('class', 'sources-ml-button button');
        });
        showThisStat(source, 'src');
    }

    function srcOutGrid(source) {
        graphics.svg.selectAll('.sources-molecule').filter(function(d) {
            if (d.source == source) {
                return this;
            }
        }).attr('class', 'sources-molecule moving');
        // inactivate button
        $('.sources-src-button').each(function() {
            console.log($(this).attr('src'));
            if ($(this).attr('src') == source) {
                $(this).attr('class', 'sources-src-button button');
            }
        });
        hideThisStats(source, 'src');
    }

    // molecule to grid functions

    function mlGridToggle(molecule) {
        if (mlStatus[molecule]) {
            mlOutGrid();
            setTrue(mlStatus, null);
        } else {
            mlInGrid(molecule);
            setTrue(mlStatus, molecule);
        }
    }

    function mlInGrid(molecule) {
        if (gridStatus === 1) {
            srcOutGrid();
        }
        gridStatus = 2;
        var sources = [false, false, false, false, false, false, false, false, false];
        graphics.svg.selectAll('.sources-molecule').filter(function(d) {
            if (d.molecule == molecule) {
                sources[d.source] = true;
                return this;
            }
        }).attr('class', 'sources-molecule').transition().duration(600).attr('transform', function(d) {
            return 'translate(' + d.gridX + ',' + (graphics.height - graphics.buffer - d.gridY) + ')';
        });
        // not selected ones
        graphics.svg.selectAll('.sources-molecule').filter(function(d) {
            if (d.molecule != molecule) {
                return this;
            }
        }).attr('class', 'sources-molecule moving');
        // buttons inactive
        $('.sources-ml-button').each(function() {
            if ($(this).attr('ml') != molecule) {
                $(this).attr('class', 'sources-ml-button inactive button');
            } else {
                $(this).attr('class', 'sources-ml-button button');
            }

        });
        showStat(molecule, 'ml');
        // highlight corresponding sources buttons
        $('.sources-src-button').each(function() {
            var src = parseInt($(this).attr('src'));
            if (sources[src]) {
                $(this).attr('class', 'sources-src-button button active');
            } else {
                $(this).attr('class', 'sources-src-button button');
            }
        });
    }

    // stats functions

    function mlOutGrid() {
        graphics.svg.selectAll('.sources-molecule').attr('class', 'sources-molecule moving');
        $('.sources-ml-button').each(function() {
            $(this).attr('class', 'sources-ml-button button');
        });
        $('.sources-src-button').each(function() {
            $(this).attr('class', 'sources-src-button button');
        });
        hideStats();
    }

    function showStat(id, modus) {
        $('.sources-stat').each(function() {
            var selector = parseInt($(this).attr(modus));

            if (selector === id) {

                var src = parseInt($(this).attr('src')),
                    ml = parseInt($(this).attr('ml'));
                d3.select(this).attr('transform', function() {
                    return 'translate(' + (sources[src].x2 + 98) + ',' + (graphics.height - graphics.buffer - statPositions[modus][ml][src]) + ')';
                });
                if (modus === 'ml') {
                    $(this).find('.sources-percentage').show();
                } else {
                    $(this).find('.sources-percentage').hide();
                }
                $(this).fadeIn(100);
            } else {
                $(this).fadeOut(100);
            }
        });
    }

    function showThisStat(id, modus) {
        $('.sources-stat').each(function() {
            var selector = parseInt($(this).attr(modus));

            if (selector == id) {

                var src = parseInt($(this).attr('src')),
                    ml = parseInt($(this).attr('ml'));
                d3.select(this).attr('transform', function() {
                    return 'translate(' + (sources[src].x2 + 98) + ',' + (graphics.height - graphics.buffer - statPositions[modus][ml][src]) + ')';
                });
                if (modus === 'ml') {
                    $(this).find('.sources-percentage').show();
                } else {
                    $(this).find('.sources-percentage').hide();
                }
                $(this).fadeIn(100);
            }
        });
    }

    function hideThisStats(id, modus) {
        $('.sources-stat').each(function() {
            var selector = parseInt($(this).attr(modus));
            if (selector == id) {
                $(this).fadeOut(100);
            }
        });
    }

    function hideStats() {
        $('.sources-stat').each(function() {
            $(this).fadeOut(100);
        });
    }

    function render() {
        buildCanvas();
        createData();
        buildSources();
        buildButtons();
        buildMolecules();
        buildStats();
    }

    render();
    play();
    window.stop = stop;
};

onMounted(draw);
</script>


<template>
    <Section id="sources">
        <h1>
            Sources of air pollution
        </h1>
        <h2>
            <span>What are the main sources of primary air pollutants?</span>
        </h2>
        <div class="text-block">
            Click on each air pollutant to see its main source or sources; or
            click on the sources to see the air pollution it causes.
        </div>

        <div
            ref="container"
            class="section__graphic">
        </div>
    </Section>
</template>


<style lang="scss">
.sources-ml-button {
    transition: opacity 0.3s ease;
}

.sources-ml-button.inactive  {
    opacity: 0.2;
}

.sources-src-button {
    cursor: pointer;
}

.sources-src-button .sources-active-area {
    fill: transparent;
    transition: fill 0.3s ease;
}

.sources-src-button.active .sources-active-area, .sources-src-button:hover .sources-active-area {
    fill: #FFCEDA;
}

.sources-stat {
    display: none;
}
</style>
